import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Grid, Stack, Tab, Tabs, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography, InputAdornment,
} from "@mui/material";
import { toast } from "react-toastify";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import SearchIcon from "@mui/icons-material/Search";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import CurrencyRupeeOutlinedIcon from "@mui/icons-material/CurrencyRupeeOutlined";
import PeopleOutlineOutlinedIcon from "@mui/icons-material/PeopleOutlineOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import api from "../../api/client";
import { PageHeader, SectionCard, StatCard, EmptyState } from "../ui";
import { extractErrorMessage } from "../../utils/errorMessage";

const money = (n) => `₹${(parseFloat(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const ACCENTS = ["#6C50BF", "#39C645", "#F9BC06", "#5C85FF", "#FF6B6B", "#14B8A6", "#EE82EE"];

const CATEGORY_LABEL = {
  lodging: "Lodging", boarding: "Boarding", food: "Food", conveyance: "Conveyance",
  Material_Purchase: "Materials", materialPurchase: "Materials", PO: "Purchase Order", Invoice: "Invoice", other: "Other",
  totalLodging: "Lodging", totalBoarding: "Boarding", totalFood: "Food",
  totalConveyance: "Conveyance", totalRND: "Materials", totalMaterialPurchase: "Materials", totalPO: "Purchase Order",
  totalInvoice: "Invoice", totalOther: "Other", totalExpenses: "Grand Total",
};

function BarList({ items, color }) {
  const max = Math.max(...items.map((i) => i.value), 1);
  return (
    <Stack spacing={1.5}>
      {items.map((item, i) => (
        <Box key={i}>
          <Stack direction="row" justifyContent="space-between" mb={0.5}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, textTransform: "capitalize" }}>{item.label}</Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color }}>{money(item.value)}</Typography>
          </Stack>
          <Box sx={{ height: 8, borderRadius: 4, bgcolor: "action.hover", overflow: "hidden" }}>
            <Box sx={{ width: `${(item.value / max) * 100}%`, height: "100%", bgcolor: color, transition: "width 0.4s" }} />
          </Box>
        </Box>
      ))}
    </Stack>
  );
}

export default function ExpenseDashboardNew() {
  const [loading, setLoading] = useState(true);
  const [byEmployee, setByEmployee] = useState([]);
  const [byType, setByType] = useState({});
  const [overall, setOverall] = useState(0);
  const [byEmpCategory, setByEmpCategory] = useState([]);
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [e, t, o, ec] = await Promise.all([
          api.get("/api/reimbursement/expenses/total-by-employee"),
          api.get("/api/reimbursement/expenses/total-by-type"),
          api.get("/api/reimbursement/expenses/overall-total"),
          api.get("/api/reimbursement/expenses/expenses-by-employee-category"),
        ]);
        setByEmployee(Array.isArray(e.data) ? e.data : e.data?.data || []);
        setByType(t.data || {});
        setOverall(o.data?.overallTotal || o.data?.totalExpenses || o.data?.total || 0);
        setByEmpCategory(Array.isArray(ec.data) ? ec.data : ec.data?.data || []);
      } catch (err) {
        toast.error(extractErrorMessage(err));
      } finally { setLoading(false); }
    })();
  }, []);

  const typeItems = useMemo(
    () => Object.entries(byType)
      .filter(([k, v]) => v && k !== "totalExpenses")
      .map(([k, v]) => ({ label: CATEGORY_LABEL[k] || k, value: parseFloat(v) || 0 }))
      .sort((a, b) => b.value - a.value),
    [byType]
  );

  const empLabel = (e) => {
    const name = e.employeeName || "";
    const code = e.friendlyEmployeeId || "";
    if (name && code) return `${name} (${code})`;
    return name || e._id || "";
  };

  const filteredEmployees = useMemo(() => {
    const q = search.toLowerCase();
    return byEmployee.filter((e) => !q || empLabel(e).toLowerCase().includes(q));
  }, [byEmployee, search]);

  const topEmployees = useMemo(
    () => [...byEmployee]
      .map((e) => ({ label: empLabel(e), value: parseFloat(e.totalExpenses || e.total || 0) || 0 }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8),
    [byEmployee]
  );

  const exportCsv = () => {
    const rows = [["Employee", "Total Expenses", ...Object.keys(byEmpCategory[0] || {}).filter((k) => k.startsWith("total") && k !== "totalExpenses").map((k) => CATEGORY_LABEL[k] || k)]];
    byEmpCategory.forEach((e) => {
      rows.push([
        empLabel(e),
        e.totalExpenses || 0,
        ...Object.keys(e).filter((k) => k.startsWith("total") && k !== "totalExpenses").map((k) => e[k] || 0),
      ]);
    });
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "expense-dashboard.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box p={3}>
      <PageHeader
        title="Expense Dashboard"
        subtitle="Organization-wide reimbursement analytics"
        icon={<AssessmentOutlinedIcon />}
        actions={[
          <Button key="export" variant="outlined" startIcon={<DownloadOutlinedIcon />} onClick={exportCsv}>
            Export CSV
          </Button>,
        ]}
      />

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
      ) : (
        <>
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} md={3}><StatCard label="Total Spend" value={money(overall)} helperText="All time" icon={<CurrencyRupeeOutlinedIcon />} color="primary" /></Grid>
            <Grid item xs={12} md={3}><StatCard label="Employees" value={byEmployee.length} helperText="With claims" icon={<PeopleOutlineOutlinedIcon />} color="success" /></Grid>
            <Grid item xs={12} md={3}><StatCard label="Categories" value={typeItems.length} helperText="Expense types" icon={<CategoryOutlinedIcon />} color="warning" /></Grid>
            <Grid item xs={12} md={3}><StatCard label="Avg / Employee" value={byEmployee.length > 0 ? money(overall / byEmployee.length) : "—"} helperText="Mean spend" icon={<ReceiptLongOutlinedIcon />} color="info" /></Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <SectionCard title="Spend by Category" subtitle="Across all employees" accent="#6C50BF">
                {typeItems.length === 0 ? (
                  <EmptyState title="No data" subtitle="No category spend recorded yet." />
                ) : (
                  <BarList items={typeItems} color="#6C50BF" />
                )}
              </SectionCard>
            </Grid>
            <Grid item xs={12} md={6}>
              <SectionCard title="Top Spenders" subtitle="Highest claim totals" accent="#39C645">
                {topEmployees.length === 0 ? (
                  <EmptyState title="No employees" subtitle="No claims have been submitted yet." />
                ) : (
                  <BarList items={topEmployees} color="#39C645" />
                )}
              </SectionCard>
            </Grid>

            <Grid item xs={12}>
              <SectionCard
                title="Employee Breakdown"
                subtitle="Detailed spend per person and category"
                accent="#F9BC06"
                actions={[
                  <TextField
                    key="s" size="small" placeholder="Search employee…"
                    value={search} onChange={(e) => setSearch(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                    sx={{ width: 220 }}
                  />,
                ]}
              >
                <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
                  <Tab label="By Employee" />
                  <Tab label="By Category" />
                </Tabs>

                {tab === 0 && (
                  <Box sx={{ overflowX: "auto" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, fontSize: 11, textTransform: "uppercase" }}>Employee</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, textTransform: "uppercase" }}>Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredEmployees.map((e, i) => (
                          <TableRow key={i} hover>
                            <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>{empLabel(e)}</TableCell>
                            <TableCell align="right" sx={{ fontSize: 13, fontWeight: 700, color: "primary.main" }}>
                              {money(e.totalExpenses || e.total)}
                            </TableCell>
                          </TableRow>
                        ))}
                        {filteredEmployees.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={2} sx={{ textAlign: "center", color: "text.secondary", py: 3 }}>
                              No employees match "{search}"
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </Box>
                )}

                {tab === 1 && (
                  <Grid container spacing={2}>
                    {typeItems.map((item, i) => (
                      <Grid item xs={12} sm={6} md={3} key={item.label}>
                        <Box sx={{ p: 2, borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
                          <Chip size="small" label={item.label} sx={{ mb: 1, bgcolor: `${ACCENTS[i % ACCENTS.length]}20`, color: ACCENTS[i % ACCENTS.length], fontWeight: 700 }} />
                          <Typography sx={{ fontSize: 22, fontWeight: 800, color: ACCENTS[i % ACCENTS.length] }}>{money(item.value)}</Typography>
                          <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                            {overall > 0 ? `${((item.value / overall) * 100).toFixed(1)}% of total` : ""}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </SectionCard>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
}
